// Feed Item Renderer Component
// Renders different types of feed items with TikTok-like styling

import React from "react";
import { motion } from "framer-motion";
import {
  FeedItemContent,
  MediaContainer,
  MediaElement,
  FeedImage,
  FeedVideo,
  ContentOverlay,
  UserInfo,
  UserAvatar,
  UserDetails,
  Username,
  Timestamp,
  ContentText,
  ContentTitle,
  ContentDescription,
  ActionButtons,
  ActionButton,
  ActionCount,
  SwipeModeToggle,
} from "../../../styles/SocialFeed/FeedContainer.styles";
import { FeedItem, FeedInteraction } from "../../../types/socialFeed";
import { HeartGamesItem } from "./HeartGamesItem";
import { CompatibilityItem } from "./CompatibilityItem";
import { SocialActivityItem } from "./SocialActivityItem";
import { CommunityChallengeItem } from "./CommunityChallengeItem";
import { MatchAnnouncementItem } from "./MatchAnnouncementItem";
import { logger } from '../../../utils/logger';

interface FeedItemRendererProps {
  item: FeedItem;
  onInteraction: (interaction: FeedInteraction) => void;
  onUserClick: (userId: string) => void;
  onSwipeModeTrigger?: (item: FeedItem) => void;
  showSwipeOption?: boolean;
  isFullScreen?: boolean;
  aspectRatio?: number;
}

export const FeedItemRenderer: React.FC<FeedItemRendererProps> = ({
  item,
  onInteraction,
  onUserClick,
  onSwipeModeTrigger,
  showSwipeOption = false,
  isFullScreen = false,
  aspectRatio,
}) => {
  // Handle different content types
  const renderContentByType = () => {
    logger.log('FeedItemRenderer: renderContentByType called for item:', item.id, item.type);
    switch (item.type) {
      case "heart-games-achievement":
      case "heart-games-completion":
      case "streak-milestone":
        return (
          <HeartGamesItem
            item={item as any}
            onInteraction={onInteraction}
            onUserClick={onUserClick}
            onSwipeModeTrigger={onSwipeModeTrigger}
            showSwipeOption={showSwipeOption}
            isFullScreen={isFullScreen}
            aspectRatio={aspectRatio}
          />
        );

      case "compatibility-suggestion":
        return (
          <CompatibilityItem
            item={item as any}
            onInteraction={onInteraction}
            onUserClick={onUserClick}
            onSwipeModeTrigger={onSwipeModeTrigger}
            showSwipeOption={showSwipeOption}
            isFullScreen={isFullScreen}
            aspectRatio={aspectRatio}
          />
        );

      case "social-activity":
      case "profile-update":
        return (
          <SocialActivityItem
            item={item}
            onInteraction={onInteraction}
            onUserClick={onUserClick}
            onSwipeModeTrigger={onSwipeModeTrigger}
            showSwipeOption={showSwipeOption}
            isFullScreen={isFullScreen}
            aspectRatio={aspectRatio}
          />
        );

      case "community-challenge":
        return (
          <CommunityChallengeItem
            item={item}
            onInteraction={onInteraction}
            onUserClick={onUserClick}
            onSwipeModeTrigger={onSwipeModeTrigger}
            showSwipeOption={showSwipeOption}
            isFullScreen={isFullScreen}
            aspectRatio={aspectRatio}
          />
        );

      case "match-announcement":
        return (
          <MatchAnnouncementItem
            item={item}
            onInteraction={onInteraction}
            onUserClick={onUserClick}
            onSwipeModeTrigger={onSwipeModeTrigger}
            showSwipeOption={showSwipeOption}
            isFullScreen={isFullScreen}
            aspectRatio={aspectRatio}
          />
        );

      case "leaderboard-update":
        return renderDefaultContent();

      case "video-content":
      case "image-content":
      case "text-content":
        return renderMediaContent();

      default:
        logger.log('FeedItemRenderer: Unhandled item type:', item.type, item);
        return renderDefaultContent();
    }
  };

  // Render media content (TikTok-style)
  const renderMediaContent = () => (
    <FeedItemContent>
      {/* Media Container */}
      {item.content.media && (
        <MediaContainer aspectRatio={item.content.media.aspectRatio}>
          <MediaElement 
            type={item.content.media.type} 
            aspectRatio={item.content.media.aspectRatio}
          >
            {item.content.media.type === "video" ? (
              <FeedVideo
                src={item.content.media.url}
                poster={item.content.media.thumbnail}
                autoPlay
                muted
                loop
                playsInline
              />
            ) : (
              <FeedImage
                src={item.content.media.url}
                alt={item.content.media.alt || item.content.title}
              />
            )}
          </MediaElement>
        </MediaContainer>
      )}

      {/* Content Overlay */}
      <ContentOverlay>
        <UserInfo>
          <UserAvatar 
            src={item.avatar} 
            alt={item.username}
            onClick={() => onUserClick(item.userId)}
          />
          <UserDetails>
            <Username onClick={() => onUserClick(item.userId)}>
              {item.username}
            </Username>
            <Timestamp>
              {formatTimestamp(item.timestamp)}
            </Timestamp>
          </UserDetails>
        </UserInfo>

        <ContentText>
          {item.content.title && (
            <ContentTitle>{item.content.title}</ContentTitle>
          )}
          {item.content.description && (
            <ContentDescription>{item.content.description}</ContentDescription>
          )}
        </ContentText>
      </ContentOverlay>

      {/* Action Buttons */}
      <ActionButtons>
        <ActionButton
          $variant="primary"
          $isActive={item.engagement.isLiked}
          onClick={() => onInteraction({
            itemId: item.id,
            userId: item.userId,
            type: "like",
            timestamp: new Date(),
          })}
        >
          {item.engagement.isLiked ? "❤️" : "🤍"}
        </ActionButton>
        <ActionCount>{item.engagement.likes}</ActionCount>

        <ActionButton
          onClick={() => onInteraction({
            itemId: item.id,
            userId: item.userId,
            type: "comment",
            timestamp: new Date(),
          })}
        >
          💬
        </ActionButton>
        <ActionCount>{item.engagement.comments}</ActionCount>

        <ActionButton
          onClick={() => onInteraction({
            itemId: item.id,
            userId: item.userId,
            type: "share",
            timestamp: new Date(),
          })}
        >
          📤
        </ActionButton>
        <ActionCount>{item.engagement.shares}</ActionCount>
      </ActionButtons>

      {/* Swipe Mode Toggle */}
      {showSwipeOption && onSwipeModeTrigger && (
        <SwipeModeToggle
          $show={true}
          onClick={() => onSwipeModeTrigger(item)}
        >
          💕 Swipe Mode
        </SwipeModeToggle>
      )}
    </FeedItemContent>
  );

  // Render default content for unsupported types
  const renderDefaultContent = () => {
    logger.log('FeedItemRenderer: renderDefaultContent called for item:', item.id, item.type, item);
    return (
    <FeedItemContent>
      {/* Background for default content */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `linear-gradient(135deg, #ff8c42 0%, #ffb366 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1
      }}>
        <div style={{
          color: '#2c3e50',
          textAlign: 'center',
          padding: '24px',
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '16px',
          maxWidth: '85%',
          border: '2px solid rgba(255, 140, 66, 0.3)',
          fontSize: '16px',
          fontWeight: '600',
          boxShadow: '0 8px 32px rgba(255, 140, 66, 0.2)'
        }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '22px', color: '#2c3e50', fontWeight: '700' }}>
            {item.content.title || 'Feed Item'}
          </h3>
          <p style={{ margin: '0 0 12px 0', fontSize: '16px', color: '#5a6c7d', lineHeight: '1.5' }}>
            {item.content.description || 'Default content'}
          </p>
          <p style={{ margin: '0', fontSize: '14px', color: '#ff8c42', fontWeight: '600' }}>
            {item.username} • {item.type}
          </p>
        </div>
      </div>
      
      <ContentOverlay>
        <UserInfo>
          <UserAvatar 
            src={item.avatar} 
            alt={item.username}
            onClick={() => onUserClick(item.userId)}
          />
          <UserDetails>
            <Username onClick={() => onUserClick(item.userId)}>
              {item.username}
            </Username>
            <Timestamp>
              {formatTimestamp(item.timestamp)}
            </Timestamp>
          </UserDetails>
        </UserInfo>

        <ContentText>
          {item.content.title && (
            <ContentTitle>{item.content.title}</ContentTitle>
          )}
          {item.content.description && (
            <ContentDescription>{item.content.description}</ContentDescription>
          )}
        </ContentText>
      </ContentOverlay>

      <ActionButtons>
        <ActionButton
          $variant="primary"
          $isActive={item.engagement.isLiked}
          onClick={() => onInteraction({
            itemId: item.id,
            userId: item.userId,
            type: "like",
            timestamp: new Date(),
          })}
        >
          {item.engagement.isLiked ? "❤️" : "🤍"}
        </ActionButton>
        <ActionCount>{item.engagement.likes}</ActionCount>

        <ActionButton
          onClick={() => onInteraction({
            itemId: item.id,
            userId: item.userId,
            type: "comment",
            timestamp: new Date(),
          })}
        >
          💬
        </ActionButton>
        <ActionCount>{item.engagement.comments}</ActionCount>

        <ActionButton
          onClick={() => onInteraction({
            itemId: item.id,
            userId: item.userId,
            type: "share",
            timestamp: new Date(),
          })}
        >
          📤
        </ActionButton>
        <ActionCount>{item.engagement.shares}</ActionCount>
      </ActionButtons>
    </FeedItemContent>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      {renderContentByType()}
    </motion.div>
  );
};

// Helper function to format timestamp
const formatTimestamp = (timestamp: Date | string): string => {
  const now = new Date();
  const time = new Date(timestamp);
  const diff = now.getTime() - time.getTime();
  
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return "now";
  if (minutes < 60) return `${minutes}m`;
  if (hours < 24) return `${hours}h`;
  if (days < 7) return `${days}d`;
  
  return new Date(timestamp).toLocaleDateString();
};
